import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import {
  USE_CASE_FLOWS,
  getUseCaseFlow,
  isUseCaseFlowId,
  nodeById,
} from "./flows";
import { getInternalSteps } from "./internalSteps";
import AnimatedFlowCanvas from "./AnimatedFlowCanvas";
import StepKeyPanel from "./StepKeyPanel";

const SUB_STEP_MS = 900;
const HOLD_AFTER_MS = 700;

const RELATED = [
  ["/docs/data-sources", "docs.toc.data-sources"],
  ["/docs/data-prep", "docs.toc.data-prep"],
  ["/docs/ai-context", "docs.toc.ai-context"],
  ["/docs/report", "docs.toc.report"],
  ["/docs/widgets", "docs.toc.widgets"],
  ["/docs/anomalies", "docs.toc.anomalies"],
  ["/docs/scheduled-reports", "docs.toc.scheduled-reports"],
] as const;

export default function UseCaseFlowsView() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const hashId = location.hash.replace(/^#/, "");
  const activeFlow = useMemo(
    () => getUseCaseFlow(isUseCaseFlowId(hashId) ? hashId : undefined),
    [hashId]
  );

  const [pathIndex, setPathIndex] = useState(0);
  const [subStepIndex, setSubStepIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  const path = activeFlow.path;
  const activeNodeId = path[pathIndex] ?? path[0];
  const activeNode = nodeById(activeFlow, activeNodeId);

  const internalSteps = useMemo(
    () => (activeNode ? getInternalSteps(activeFlow, activeNode, t) : []),
    [activeFlow, activeNode, t]
  );

  useEffect(() => {
    setPathIndex(0);
    setSubStepIndex(0);
    setPlaying(true);
  }, [activeFlow.id]);

  useEffect(() => {
    setSubStepIndex(0);
  }, [pathIndex]);

  useEffect(() => {
    if (!playing || internalSteps.length === 0) return undefined;

    if (subStepIndex < internalSteps.length - 1) {
      const timer = window.setTimeout(() => {
        setSubStepIndex((i) => i + 1);
      }, SUB_STEP_MS);
      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(() => {
      setPathIndex((i) => (i + 1) % path.length);
      setSubStepIndex(0);
    }, HOLD_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [playing, pathIndex, subStepIndex, internalSteps.length, path.length]);

  const selectFlow = (id: string) => {
    navigate(`/docs/use-case-flows#${id}`, { replace: true });
  };

  const goToStep = (index: number) => {
    setPlaying(false);
    setPathIndex(index);
    setSubStepIndex(0);
  };

  const selectNodeOnCanvas = (nodeId: string) => {
    const idx = path.indexOf(nodeId);
    if (idx >= 0) goToStep(idx);
  };

  const restart = () => {
    setPathIndex(0);
    setSubStepIndex(0);
    setPlaying(true);
  };

  const stepLabel = activeNode
    ? t(`docs.useCaseFlows.nodes.${activeNode.labelKey}`)
    : "";

  return (
    <Box id="overview" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h4" fontWeight={700} sx={{ mt: 0, mb: 0.75 }}>
          {t("docs.useCaseFlows.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.6 }}>
          {t("docs.useCaseFlows.intro")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          overflowX: "auto",
          py: 0.5,
        }}
        role="tablist"
        aria-label={t("docs.useCaseFlows.title")}
      >
        {USE_CASE_FLOWS.map((flow) => {
          const selected = flow.id === activeFlow.id;
          return (
            <Box
              key={flow.id}
              component="button"
              type="button"
              role="tab"
              id={flow.id}
              aria-selected={selected}
              onClick={() => selectFlow(flow.id)}
              sx={(th) => {
                const dark = th.palette.mode === "dark";
                const selectedFg = dark ? th.palette.primary.light : th.palette.primary.main;
                const idleFg = dark ? th.palette.common.white : th.palette.text.primary;
                const idleBg = dark
                  ? alpha(th.palette.common.white, 0.08)
                  : alpha(th.palette.text.primary, 0.04);
                const selectedBg = dark
                  ? alpha(th.palette.primary.light, 0.18)
                  : alpha(th.palette.primary.main, 0.1);
                return {
                  // Kill UA button paint (white bg) that breaks dark theme contrast.
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  flex: "0 0 auto",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 1,
                  m: 0,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: selected
                    ? selectedFg
                    : dark
                      ? alpha(th.palette.common.white, 0.22)
                      : th.palette.divider,
                  backgroundColor: selected ? selectedBg : idleBg,
                  color: selected ? selectedFg : idleFg,
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: selected ? 700 : 600,
                  lineHeight: 1.3,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                  "&:hover": {
                    backgroundColor: selected
                      ? dark
                        ? alpha(th.palette.primary.light, 0.26)
                        : alpha(th.palette.primary.main, 0.14)
                      : dark
                        ? alpha(th.palette.common.white, 0.14)
                        : alpha(th.palette.text.primary, 0.08),
                    color: selected ? selectedFg : idleFg,
                  },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: selectedFg,
                    outlineOffset: 2,
                  },
                };
              }}
            >
              <Icon sx={{ fontSize: 18, color: "inherit" }}>{flow.icon}</Icon>
              {t(`docs.useCaseFlows.cases.${flow.titleKey}`)}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 1.75,
          py: 1.25,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: (th) =>
            alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.08 : 0.03),
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {t(`docs.useCaseFlows.cases.${activeFlow.titleKey}`)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(`docs.useCaseFlows.cases.${activeFlow.summaryKey}`)}
            {" · "}
            {t("docs.useCaseFlows.stepOf", {
              current: pathIndex + 1,
              total: path.length,
              label: stepLabel,
            })}
            {internalSteps.length > 0 &&
              ` · ${subStepIndex + 1}/${internalSteps.length}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={playing ? t("docs.useCaseFlows.pause") : t("docs.useCaseFlows.play")}>
            <IconButton
              size="small"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? t("docs.useCaseFlows.pause") : t("docs.useCaseFlows.play")}
              sx={{
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Icon fontSize="small">{playing ? "pause" : "play_arrow"}</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title={t("docs.useCaseFlows.restart")}>
            <IconButton
              size="small"
              onClick={restart}
              aria-label={t("docs.useCaseFlows.restart")}
              sx={{
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Icon fontSize="small">replay</Icon>
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            onClick={() => goToStep(Math.max(0, pathIndex - 1))}
            disabled={pathIndex === 0}
          >
            {t("docs.useCaseFlows.prev")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => goToStep(Math.min(path.length - 1, pathIndex + 1))}
            disabled={pathIndex >= path.length - 1}
          >
            {t("docs.useCaseFlows.next")}
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.35fr) minmax(280px, 0.85fr)" },
          gap: 2,
          alignItems: "stretch",
          minHeight: { lg: 520 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
          <AnimatedFlowCanvas
            flow={activeFlow}
            pathIndex={pathIndex}
            subStepIndex={subStepIndex}
            onSelectNode={selectNodeOnCanvas}
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            height: { xs: 440, lg: "min(640px, calc(100vh - 220px))" },
            maxHeight: { xs: 440, lg: "calc(100vh - 220px)" },
            position: { lg: "sticky" },
            top: { lg: 88 },
            alignSelf: { lg: "start" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <StepKeyPanel
            flow={activeFlow}
            pathIndex={pathIndex}
            subStepIndex={subStepIndex}
          />
        </Box>
      </Box>

      <Box sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
          {t("docs.useCaseFlows.relatedDocs")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {RELATED.map(([to, key]) => (
            <Button
              key={to}
              component={RouterLink}
              to={to}
              size="small"
              variant="text"
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              {t(key)}
            </Button>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
